import { SlimOrganization, SlimGroup } from '@ilteducation/my-account-types';
import { GetUserResponse } from '@ilteducation/customer-mgmt-types';
import {
  checkAssociationUpdatePermission,
  checkUserCreatePermission,
  getAllowedRoles,
  isSuperUser,
  Reason,
  Role,
} from '@ilteducation/customer-mgmt-permissions';
import { usePermissions } from './usePermissions';

/**
 * A role, together with whether the caller may pick it and — when they may not — why not.
 * The reason is what lets the UI put a padlock with an explanation on the option instead of
 * silently hiding or greying it out.
 *
 * Only roles that can exist in the organization at all are offered. A padlock says "this is in
 * your way", which is true of a permission the caller could gain, and false of a role the
 * organization type does not have: no student can exist in a preschool however you log in.
 */
export type RoleOption = {
  type: Role;
  transKey: string;
  allowed: boolean;
  reason?: Reason;
};

const ROLES: ReadonlyArray<Role> = ['student', 'employee', 'admin', 'guardian'];

const transKeyFor = (role: Role) => `lcp.role_${role}`;

/** The roles worth offering for an organization, in the order the interface lists them. */
const rolesInOrganization = (organization: SlimOrganization): ReadonlyArray<Role> => {
  const allowed = getAllowedRoles(organization);
  return ROLES.filter((role) => allowed.includes(role));
};

export const useUserRoles = () => {
  const {
    permissionContext,
    getRoleFromAssociation,
    getCallerAssociation,
    getCallerAssociations,
    getGroupMemberships,
    reporterWithReason,
    createTargetAssociation,
  } = usePermissions();

  type PermissionResult = ReturnType<typeof reporterWithReason>;

  /**
   * Super users are exempt from the organization type rules by the permission checks themselves,
   * so hiding those roles from them would take away something they are allowed to do.
   */
  const rolesToOfferIn = (organization: SlimOrganization): ReadonlyArray<Role> =>
    isSuperUser(permissionContext.value) ? ROLES : rolesInOrganization(organization);

  const toOption = (role: Role, result: PermissionResult): RoleOption => ({
    type: role,
    transKey: transKeyFor(role),
    allowed: result.allowed,
    reason: result.reason,
  });

  const deniedOptions = (roles: ReadonlyArray<Role>, reason: Reason): RoleOption[] =>
    roles.map((role) => ({ type: role, transKey: transKeyFor(role), allowed: false, reason }));

  const getRoleOptionsToCreateWith = async (
    callerOrganization: SlimOrganization,
    groups?: SlimGroup[],
  ): Promise<RoleOption[]> =>
    Promise.all(
      rolesToOfferIn(callerOrganization).map(async (role) =>
        toOption(
          role,
          await checkUserCreatePermission(
            permissionContext.value,
            { firstName: 'dummy', associations: [{ organization: callerOrganization, role, groups }] },
            getCallerAssociations,
            getGroupMemberships,
            reporterWithReason,
          ),
        ),
      ),
    );

  const getRoleOptionsToUpdateTo = async (
    target: GetUserResponse,
    callerOrganization: SlimOrganization,
  ): Promise<RoleOption[]> => {
    const targetAssociation = target.associations.find((a) => a.schoolUnit.uid === callerOrganization.uid);
    if (!targetAssociation) {
      return deniedOptions(rolesToOfferIn(callerOrganization), 'not_permitted');
    }

    // A role the organization type disallows can still be the one the association already holds,
    // put there by a super user or a sync. Dropping it would leave the select showing a value it
    // does not offer, so the role in place is always listed, in its usual position rather than
    // jumping to the front. It is the derived role, since an administrator is stored as an
    // employee carrying `isAdministrator`, and carving out that stored employee would offer
    // Personal in every workplace — the one role the type exists to exclude.
    const allowed = rolesToOfferIn(callerOrganization);
    const currentRole = getRoleFromAssociation(targetAssociation);
    const rolesToOffer = ROLES.filter((role) => allowed.includes(role) || role === currentRole);

    return Promise.all(
      rolesToOffer.map(async (role) =>
        toOption(
          role,
          await checkAssociationUpdatePermission(
            permissionContext.value,
            createTargetAssociation(target, targetAssociation),
            { role },
            getCallerAssociation,
            reporterWithReason,
          ),
        ),
      ),
    );
  };

  const getRolesPossibleToCreateWith = async (
    callerOrganization: SlimOrganization,
    groups?: SlimGroup[],
  ): Promise<Role[]> =>
    (await getRoleOptionsToCreateWith(callerOrganization, groups)).filter((o) => o.allowed).map((o) => o.type);

  return {
    getRoleOptionsToCreateWith,
    getRoleOptionsToUpdateTo,
    getRolesPossibleToCreateWith,
  };
};
